<template>
  <div id="main-content">
    <div class="policy-container">
      <ApiLoading v-if="['pending', 'error'].includes(isApiLoading)" :apiError="apiFailed" class="api-loading">
        <p>{{ ErrorStrings.GENERIC_ERROR }}</p>
      </ApiLoading>

      <div v-if="isApiLoading === 'success'">
        <Alert v-if="dataFromVueStore" :message="ErrorStrings.GENERIC_ERROR" alertType="error" />
        <div class="policy-heading">Welcome back, {{ policyMemberships.firstName }}</div>
        <hr />
        <div class="policy-subheading">Which policy would you like to view?</div>
        <div>
          <div v-for="membership in policyMemberships.productList" :key="membership.memberNumber" class="policy-item">
            <div class="policy-details">
              <div class="policy-number" v-if="membership.memberNumber !== ''">{{ membership.memberNumber }}</div>
              <div class="product-name" v-if="membership.productName !== ''">{{ membership.productName }}</div>
              <div class="policy-role" v-if="membership.coveredOnMembership !== ''">{{ membership.coveredOnMembership }}</div>
            </div>
            <div class="policy-button-container">
              <router-link :to="toMemberHomepage(membership.memberNumber)" class="button" @click="storePolicyNumber(membership.memberNumber)">
                Select this policy
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import { HOME } from '@/router/routeNames';
import { ApiLoading, Alert } from '@/components';
import MemberDetailsServices from '@/services/MemberDetails';
import { LoadingStatus } from '@general/js/enums';
import { ErrorStrings } from '@general/js/constants';
import { VUEX_GENERAL, VUEX_MEMBER_DETAILS } from '@/constants/VuexConstants';

// having separate variable outside component as at the stage of `beforeRouteEnter`, data are not initialised
let memberWantsToSwitchPolicy = false;

export default {
  name: 'PolicySelection',

  components: {
    ApiLoading,
    Alert,
  },

  beforeRouteEnter(to, from, next) {
    if (from.name !== null) {
      // if from route name is not null, it means member is visiting this page from other route and intend to switch policy
      memberWantsToSwitchPolicy = true;
    }
    next();
  },

  data() {
    return {
      isApiLoading: LoadingStatus.PENDING,
      apiFailed: false,
      dataFromVueStore: false,
      policyMemberships: {
        firstName: '',
        productList: [],
      },
      ErrorStrings,
      switching: memberWantsToSwitchPolicy,
    };
  },

  beforeMount() {
    this.fetchData();
  },

  mounted() {
    if (this.switching) {
      this.resetMemberDetails();
      this.resetPolicy();
    }
  },

  methods: {
    ...mapMutations([
      VUEX_GENERAL.MUTATIONS.resetPolicy,
      VUEX_MEMBER_DETAILS.MUTATIONS.resetMemberDetails,
      VUEX_GENERAL.MUTATIONS.saveThisPolicyNumber,
      VUEX_GENERAL.MUTATIONS.saveThisMembership,
    ]),

    ...mapMutations('MemberDetails', ['resetMemberDetails']),

    async fetchData() {
      try {
        let result;
        if (this.getCurrentMembership === null || this.switching) {
          result = await MemberDetailsServices.getMemberships();
        } else {
          result = this.getCurrentMembership;
        }
        this.policyMemberships = result;
      } catch (error) {
        logger.error(error);
        this.dataFromVueStore = true;
        this.setPolicyMembershipsFromStore();
      } finally {
        this.postFetchDataProcessing();
      }
    },

    postFetchDataProcessing() {
      if (this.hasSingleMembership) {
        this.navigateToSingleMembership();
      } else if (this.noMembershipsFound) {
        this.isApiLoading = LoadingStatus.ERROR;
        this.apiFailed = true;
      } else {
        this.sortPolicyByMemberNumberAscending();
        this.isApiLoading = LoadingStatus.SUCCESS;
      }
    },

    setPolicyMembershipsFromStore() {
      try {
        this.policyMemberships.firstName = this.memberDetails.firstName;
        this.policyMemberships.productList = this.getMemberships.map((membership) => {
          return { memberNumber: membership.memberNumber, coveredOnMembership: '', productName: '' };
        });
      } catch (error) {
        logger.error(error);
      }
    },

    navigateToSingleMembership() {
      const memberNumber = this.policyMemberships.productList[0].memberNumber;
      this.storePolicyNumber(memberNumber);
      this.$router.push(this.toMemberHomepage(memberNumber));
    },

    sortPolicyByMemberNumberAscending() {
      this.policyMemberships.productList.sort((a, b) => {
        return a.memberNumber > b.memberNumber ? 1 : -1;
      });
    },

    toMemberHomepage(policyNumber) {
      return {
        name: HOME,
        params: {
          policyNumber,
        },
      };
    },

    storePolicyNumber(policyNumber) {
      this.saveThisPolicyNumber(policyNumber);
    },
  },

  computed: {
    ...mapGetters({
      memberDetails: VUEX_GENERAL.GETTERS.getCurrentMemberDetails,
      getMemberships: VUEX_GENERAL.GETTERS.memberships,
      getCurrentMembership: VUEX_GENERAL.GETTERS.getCurrentMembership,
    }),

    hasSingleMembership() {
      return this.policyMemberships.productList ? this.policyMemberships.productList.length === 1 : false;
    },

    noMembershipsFound() {
      return this.policyMemberships.productList ? this.policyMemberships.productList.length === 0 : true;
    },
  },
};
</script>

<style lang="scss" scoped>
#main-content {
  margin-top: spacer(-5);
  padding: spacer(2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 1;

  &::before {
    background-size: cover;

    @include tablet-and-below {
      top: -$CONDENSED_HEADER_HEIGHT_MOBILE_TABLET;
      height: calc(100vh + #{$CONDENSED_HEADER_HEIGHT_MOBILE_TABLET});
    }

    @include desktop {
      top: -$CONDENSED_HEADER_HEIGHT_DESKTOP;
      height: calc(100% + #{$CONDENSED_HEADER_HEIGHT_DESKTOP});
    }

    width: 100vw;

    position: fixed;
    content: ' ';
    left: 0;

    z-index: -100;
    overflow: hidden;
  }

  .policy-container {
    box-shadow: 0px 2px 6px 0px rgba(22, 22, 29, 0.06), 0px 2px 4px 0px rgba(22, 22, 29, 0.02);
    padding: 48px 24px;
    background-color: $WHITE;
    border-radius: 16px;
    max-width: 530px;
    box-sizing: border-box;

    hr {
      background-color: $PRIMARY_GREEN;
      border: none;
      height: 2px;
      margin: 0 0 1rem 0;
      width: 80px;
    }

    .api-loading {
      min-width: 400px;
    }

    .policy-item {
      display: grid;
      grid-template-columns: 1fr auto;
      gap: spacer(1);
      padding-top: spacer(2.5);
      padding-bottom: spacer(2.5);
      border-bottom: 1px solid $GREY_200;

      @include mobile {
        grid-template-columns: 1fr;
      }
    }

    .policy-item:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }

    .policy-details {
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    .policy-details > div {
      margin-bottom: 8px;

      &:only-child {
        margin-bottom: 0px;
      }
    }

    .policy-heading {
      font-size: font-size(3);
      font-weight: $BOLD;
      margin-bottom: spacer(1);
    }

    .policy-subheading {
      font-size: font-size(2);
      font-weight: $BOLD;
      margin-top: 32px;
    }

    .policy-number {
      font-size: font-size(0);
    }

    .product-name {
      font-size: font-size(1);
      font-weight: $BOLD;
    }

    .policy-role {
      font-size: font-size(-2);
      margin-bottom: 0;
    }

    .policy-button-container {
      display: flex;
      align-items: center;
      @include mobile {
        width: 100%;
      }

      & > a.button {
        min-height: 48px;
        min-width: 201px;
        display: inline-flex;
        justify-content: center;
        align-items: center;
      }
    }

    @include mobile {
      padding: spacer(3) spacer(2);
    }
  }
}
</style>
